import { CSSProperties } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { avatarSlotFor } from '../../../lib/avatar-color';
import { Avatar, Badge, Button, Card } from '../ui';

// Home 2 (symphonyHome2Web) — the full-width Outreach section (Figma 1859:2860).
//
// ⚠️ MOCK: this renders a hardcoded placeholder list under a diagonal "MOCK" watermark — there is no
// wired outreach data source for this card yet. Visible only behind the FT (dev/override-only). Replace
// MOCK_OUTREACH + the stamp with the real hook and localize the row copy when a source lands. Per the
// Figma annotation, only "Needs you" outreaches are shown in detail.

interface MockOutreach {
  initials: string;
  name: string;
  channel?: string;
  time?: string;
  quote?: string;
}

// Placeholder content only — not real product copy (see the MOCK note above).
const MOCK_OUTREACH: MockOutreach[] = [
  {
    initials: 'NB',
    name: 'Noa Bar',
    channel: 'WhatsApp',
    time: '2d ago',
    quote: '“Can we move to Tuesday afternoon instead?”',
  },
  { initials: 'YC', name: 'Yonatan Cohen' },
];
const MOCK_NEEDS_YOU = 2;
const MOCK_SENT_TODAY = 12;

const SECTION: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 16, width: '100%' };
const HEADER: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' };
const TITLE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 16,
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'rgb(var(--ink-rgb))',
};
const CARD: CSSProperties = {
  borderColor: 'var(--ds-separator-default)',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  position: 'relative',
  overflow: 'hidden',
};
const ROW: CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' };
const ROW_LEFT: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 };
const NAME: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 13,
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
const TIME: CSSProperties = { fontFamily: 'var(--brand-font-ui)', fontSize: 12, color: 'var(--ds-text-secondary)', flexShrink: 0 };
const QUOTE: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 12,
  color: 'rgb(var(--ink-rgb))',
  letterSpacing: '-0.24px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};
const DIVIDER: CSSProperties = { height: 1, width: '100%', background: 'var(--ds-separator-light)', flexShrink: 0 };
const FOOTER: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 12,
  fontWeight: 'var(--sym-weight-medium)',
  color: 'var(--ds-text-secondary)',
  textTransform: 'uppercase',
};

const STAMP: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 2,
};
const STAMP_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 72,
  fontWeight: 800,
  letterSpacing: '0.15em',
  color: 'rgba(var(--ds-text-destructive-rgb), 0.16)',
  transform: 'rotate(-18deg)',
  userSelect: 'none',
};

export function Home2OutreachSection() {
  const { t } = useTranslation();

  return (
    <div style={SECTION}>
      <div style={HEADER}>
        <span style={TITLE}>{t('home2.outreach.title')}</span>
        {/* Inert while this section is mock — no outreach destination wired yet. */}
        <Button size="medium" onClick={() => undefined}>
          {t('home2.outreach.open')}
        </Button>
      </div>

      <Card style={CARD}>
        <div aria-hidden style={STAMP}>
          <span style={STAMP_TEXT}>MOCK</span>
        </div>

        {MOCK_OUTREACH.map((o) => (
          <div key={o.name} style={ROW}>
            <span style={ROW_LEFT}>
              <Avatar initials={o.initials} size={44} color={avatarSlotFor(o.name)} />
              <span style={NAME}>{o.name}</span>
              {o.channel && <Badge variant="agents" label={o.channel} size="tiny" />}
              {o.time && <span style={TIME}>{`· ${o.time}`}</span>}
              {o.quote && <span style={QUOTE}>{o.quote}</span>}
            </span>
            <Badge variant="waiting-on-you" label={t('home2.tasks.needsYou')} size="tiny" />
          </div>
        ))}

        <div style={DIVIDER} />

        <span style={FOOTER}>
          {t('home2.outreach.summary', { needsYou: MOCK_NEEDS_YOU, sent: MOCK_SENT_TODAY })}
        </span>
      </Card>
    </div>
  );
}
